import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import CookieBanner from '@/components/layout/CookieBanner';
import ScrollToTop from '@/components/layout/ScrollToTop';
import PageTransition from '@/components/layout/PageTransition';
import PageAnimations from '@/components/layout/PageAnimations';

import HomePage from '@/pages/HomePage';
import CompetitionPage from '@/pages/CompetitionPage';
import HowItWorksPage from '@/pages/HowItWorksPage';
import WinnersPage from '@/pages/WinnersPage';
import WinnerStoryPage from '@/pages/WinnerStoryPage';
import CompetitionsArchivePage from '@/pages/CompetitionsArchivePage';
import AboutPage from '@/pages/AboutPage';
import FAQPage from '@/pages/FAQPage';
import TermsPage from '@/pages/TermsPage';
import PrivacyPage from '@/pages/PrivacyPage';
import SaferPlayPage from '@/pages/SaferPlayPage';
import HowWeDrawWinnersPage from '@/pages/HowWeDrawWinnersPage';
import PastDrawsPage from '@/pages/PastDrawsPage';
import VerifyDrawPage from '@/pages/VerifyDrawPage';
import GivingBackPage from '@/pages/GivingBackPage';
import DataRequestPage from '@/pages/DataRequestPage';
import ContactPage from '@/pages/ContactPage';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import UnsubscribePage from '@/pages/UnsubscribePage';
import PaymentSuccessPage from '@/pages/PaymentSuccessPage';
import PaymentFailedPage from '@/pages/PaymentFailedPage';
import PaymentCancelledPage from '@/pages/PaymentCancelledPage';
import AccountPage from '@/pages/AccountPage';

import AdminLoginPage from '@/pages/admin/AdminLoginPage';
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminCompetitions from '@/pages/admin/AdminCompetitions';
import AdminHomepage from '@/pages/admin/AdminHomepage';
import AdminCompetitionEditor from '@/pages/admin/AdminCompetitionEditor';
import AdminEntries from '@/pages/admin/AdminEntries';
import AdminWinners from '@/pages/admin/AdminWinners';
import AdminDraws from '@/pages/admin/AdminDraws';
import AdminPostal from '@/pages/admin/AdminPostal';
import AdminWaitlist from '@/pages/admin/AdminWaitlist';
import AdminCharity from '@/pages/admin/AdminCharity';
import AdminCustomers from '@/pages/admin/AdminCustomers';
import AdminBroadcast from '@/pages/admin/AdminBroadcast';
import AdminPartnerCodes from '@/pages/admin/AdminPartnerCodes';
import AdminPartnerBatchDetail from '@/pages/admin/AdminPartnerBatchDetail';

const PublicShell = ({ children }) => (
  <div className="min-h-screen flex flex-col">
    <Header />
    <main className="flex-1">
      <PageTransition>{children}</PageTransition>
    </main>
    <Footer />
    <CookieBanner />
  </div>
);

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <PageAnimations />
      <Toaster theme="dark" position="top-center" richColors />
      <Routes>
        {/* Public */}
        <Route path="/" element={<PublicShell><HomePage /></PublicShell>} />
        <Route path="/competitions" element={<PublicShell><CompetitionsArchivePage /></PublicShell>} />
        <Route path="/competitions/:slug" element={<PublicShell><CompetitionPage /></PublicShell>} />
        <Route path="/how-it-works" element={<PublicShell><HowItWorksPage /></PublicShell>} />
        <Route path="/winners" element={<PublicShell><WinnersPage /></PublicShell>} />
        <Route path="/winners/:id" element={<PublicShell><WinnerStoryPage /></PublicShell>} />
        <Route path="/about" element={<PublicShell><AboutPage /></PublicShell>} />
        <Route path="/faq" element={<PublicShell><FAQPage /></PublicShell>} />
        <Route path="/terms" element={<PublicShell><TermsPage /></PublicShell>} />
        <Route path="/privacy" element={<PublicShell><PrivacyPage /></PublicShell>} />
        <Route path="/safer-play" element={<PublicShell><SaferPlayPage /></PublicShell>} />
        <Route path="/how-we-draw-winners" element={<PublicShell><HowWeDrawWinnersPage /></PublicShell>} />
        <Route path="/past-draws" element={<PublicShell><PastDrawsPage /></PublicShell>} />
        <Route path="/past-draws/:id/verify" element={<PublicShell><VerifyDrawPage /></PublicShell>} />
        <Route path="/giving-back" element={<PublicShell><GivingBackPage /></PublicShell>} />
        <Route path="/data-request" element={<PublicShell><DataRequestPage /></PublicShell>} />
        <Route path="/contact" element={<PublicShell><ContactPage /></PublicShell>} />
        <Route path="/login" element={<PublicShell><LoginPage /></PublicShell>} />
        <Route path="/register" element={<PublicShell><RegisterPage /></PublicShell>} />
        <Route path="/forgot-password" element={<PublicShell><ForgotPasswordPage /></PublicShell>} />
        <Route path="/reset-password" element={<PublicShell><ResetPasswordPage /></PublicShell>} />
        <Route path="/unsubscribe/:token" element={<PublicShell><UnsubscribePage /></PublicShell>} />
        <Route path="/payment/success" element={<PublicShell><PaymentSuccessPage /></PublicShell>} />
        <Route path="/payment/failed" element={<PublicShell><PaymentFailedPage /></PublicShell>} />
        <Route path="/account" element={<PublicShell><AccountPage /></PublicShell>} />

        {/* Admin */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="competitions" element={<AdminCompetitions />} />
          <Route path="homepage" element={<AdminHomepage />} />
          <Route path="competitions/new" element={<AdminCompetitionEditor />} />
          <Route path="competitions/:id" element={<AdminCompetitionEditor />} />
          <Route path="competitions/:id/entries" element={<AdminEntries />} />
          <Route path="winners" element={<AdminWinners />} />
          <Route path="draws" element={<AdminDraws />} />
          <Route path="postal" element={<AdminPostal />} />
          <Route path="waitlist" element={<AdminWaitlist />} />
          <Route path="charity" element={<AdminCharity />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="broadcast" element={<AdminBroadcast />} />
          <Route path="partner-codes" element={<AdminPartnerCodes />} />
          <Route path="partner-codes/:id" element={<AdminPartnerBatchDetail />} />
        </Route>

        <Route path="*" element={<PublicShell><HomePage /></PublicShell>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
